<template>
  <div>
    <HeroHeader
      eyebrow="COMPROBANTES"
      title="Ventas"
      :counter-num="scopedList.length"
      counter-unit="ventas"
    >
      <template #toolbar>
        <button class="btn btn--small" @click="exportToExcel">Exportar Excel</button>
        <button class="btn" @click="openNewSale">+ Agregar</button>
      </template>
    </HeroHeader>

    <div class="stat-strip">
      <div class="stat-strip__item" v-for="t in periodTotals" :key="t.code">
        <span class="stat-strip__label">{{ periodTotals.length > 1 ? 'Vendido · ' + t.code : 'Total vendido' }}</span>
        <span class="stat-strip__value">{{ t.label }}</span>
      </div>
      <div class="stat-strip__item">
        <span class="stat-strip__label">Ventas</span>
        <span class="stat-strip__value">{{ scopedList.length }}</span>
      </div>
      <div class="stat-strip__item">
        <span class="stat-strip__label">Expositores</span>
        <span class="stat-strip__value">{{ tenantsCount }}</span>
      </div>
    </div>

    <DateFilter v-model="preset" @change="onDateFilterChange" />

    <div class="filter-bar filter-bar--no-top">
      <SearchInput v-model="search" placeholder="Buscar expositor…"/>
      <select v-model="filterLocal">
        <option value="All">Todas las propiedades</option>
        <option v-for="e in estates" :key="e.id" :value="e.id">{{ e.description }}</option>
      </select>
      <SortSelect :options="sortOptions" v-model="sortBy" />
    </div>

    <div v-if="pagedGroups.length > 0" class="collection-list">
      <div class="collection-card" v-for="g in pagedGroups" :key="g.id">

        <!-- Cabecera de expositor -->
        <div
          class="prop-row prop-row--clickable"
          :class="{ 'prop-row--active': isExpanded(g.id) }"
          @click="toggleGroup(g.id)"
        >
          <div class="prop-row__head">
            <div class="prop-row__info">
              <div class="prop-row__name">{{ g.name }}</div>
              <div class="prop-row__address">
                {{ g.local }} · {{ g.count }} venta{{ g.count !== 1 ? 's' : '' }}
              </div>
            </div>
            <div class="prop-row__right">
              <span class="prop-row__collected">{{ g.totalLabel }}</span>
              <span class="prop-row__chevron" :class="{ 'prop-row__chevron--open': isExpanded(g.id) }">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                     fill="none" stroke="currentColor" stroke-width="2"
                     stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </span>
            </div>
          </div>
        </div>

        <!-- Detalle: ventas del expositor (expandible) -->
        <transition name="expand">
        <div v-if="isExpanded(g.id)" class="collection-sub">
          <div v-for="s in g.sales" :key="s.id" class="tenant-row">
            <div class="tenant-row__body">
              <div class="tenant-row__name">{{ s.dateOfIssue ? formatDate(s.dateOfIssue) : '—' }}</div>
              <div class="tenant-row__meta">
                {{ productCount(s) }} producto{{ productCount(s) !== 1 ? 's' : '' }}
              </div>
            </div>
            <div class="tenant-row__right">
              <span class="tenant-row__rent">{{ formatAmount(s.total, s.currency) }}</span>
            </div>
            <div class="tenant-row__actions">
              <span class="dt-icon" title="Editar" @click.stop="openUpdate(s)">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
              </span>
            </div>
          </div>
          <div class="collection-sub__foot">
            <button class="btn btn--small" @click.stop="printReport(g)">Imprimir reporte</button>
          </div>
        </div>
        </transition>

      </div>
    </div>

    <EmptyState v-else title="Sin ventas" message="No hay ventas para el periodo seleccionado."/>

    <div v-if="sortedGroups.length > pageSize" class="dt-pagination">
      <span class="dt-pagination__info">{{ pageFrom }}–{{ pageTo }} de {{ sortedGroups.length }} expositores</span>
      <div class="dt-pagination__controls">
        <button :disabled="page <= 1" @click="page--">← Anterior</button>
        <button :disabled="page >= totalPages" @click="page++">Siguiente →</button>
      </div>
    </div>

    <NewSale :clients="clients" @close="showNewSidebar = false" @add="addSidebar"
             :class="{'show': showNewSidebar}" title="Nueva venta" :show-navbar="showNewSidebar"/>
    <UpdateSale :clients="clients" :data="sidebarData" @close="showUpdateSidebar = false" @update="updateSidebar"
                :class="{'show': showUpdateSidebar}" title="Editar venta" :show-navbar="showUpdateSidebar"/>
  </div>
</template>

<script>
import { db, Timestamp } from '@/firebase/firebaseConfig'
import NewSale from './NewSale'
import UpdateSale from './UpdateSale'
import HeroHeader from '@/components/HeroHeader'
import DateFilter from '@/components/DateFilter'
import EmptyState from '@/components/EmptyState'
import SortSelect from '@/components/SortSelect'
import SearchInput from '@/components/SearchInput'
import { sortItems } from '@/utils/sort'
import { normalizeText, matches } from '@/utils/text'
import moment from 'moment'

export default {
  name: 'Sales',
  components: { NewSale, UpdateSale, HeroHeader, DateFilter, EmptyState, SortSelect, SearchInput },
  data () {
    const now = new Date()
    return {
      list: [],
      clients: [],
      clientsLoaded: false,
      estates: [],
      search: '',
      filterLocal: 'All',
      sortBy: 'total_desc',
      sortOptions: [
        { value: 'total_desc', label: 'Total vendido (mayor)', field: 'sortTotal', type: 'number', dir: 'desc' },
        { value: 'count_desc', label: 'Más ventas', field: 'count', type: 'number', dir: 'desc' },
        { value: 'recent_desc', label: 'Venta más reciente', field: 'lastSale', type: 'date', dir: 'desc' },
        { value: 'tenant', label: 'Expositor (A–Z)', field: 'name', type: 'string', dir: 'asc' }
      ],
      preset: 'month',
      dateFrom: new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0],
      dateTo: new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0],
      page: 1,
      pageSize: 20,
      expandedIds: [],
      showNewSidebar: false,
      showUpdateSidebar: false,
      sidebarData: {}
    }
  },
  computed: {
    // Ventas del periodo acotadas por el filtro de local (el "scope" del que
    // dependen los totales globales); la búsqueda por texto refina aparte.
    scopedList () {
      if (this.filterLocal === 'All') return this.list
      return this.list.filter(s => s.client && s.client.local && s.client.local.id === this.filterLocal)
    },
    filteredList () {
      const q = normalizeText(this.search)
      if (!q) return this.scopedList
      return this.scopedList.filter(o => {
        const c = o.client || {}
        const local = c.local && c.local.description ? c.local.description : ''
        return matches(c.alias, q) || matches(c.name, q) || matches(local, q)
      })
    },
    // Agrupa las ventas por expositor. Los totales se acumulan por moneda
    // (nativa, nunca se convierten ni se mezclan PEN con USD).
    groups () {
      const map = new Map()
      for (const sale of this.filteredList) {
        const c = sale.client || {}
        const key = c.id || c.document || (c.alias || c.name || 'sin-expositor')
        if (!map.has(key)) {
          map.set(key, {
            id: key,
            name: c.alias || c.name || 'Sin expositor',
            local: c.local && c.local.description ? c.local.description : '—',
            document: c.document || '',
            sales: [],
            totals: {}, // { PEN: n, USD: n } — por moneda
            sortTotal: 0, // suma cruda, solo para ordenar
            lastSale: null
          })
        }
        const g = map.get(key)
        g.sales.push(sale)
        const code = sale.currency && sale.currency.code ? sale.currency.code : 'PEN'
        g.totals[code] = (g.totals[code] || 0) + (sale.total || 0)
        g.sortTotal += (sale.total || 0)
        if (sale.dateOfIssue && (!g.lastSale || sale.dateOfIssue > g.lastSale)) g.lastSale = sale.dateOfIssue
      }
      const arr = [...map.values()]
      arr.forEach(g => {
        g.count = g.sales.length
        g.products = g.sales.reduce((a, s) => a + this.productCount(s), 0)
        g.sales.sort((a, b) => (b.dateOfIssue ? b.dateOfIssue.getTime() : 0) - (a.dateOfIssue ? a.dateOfIssue.getTime() : 0))
        g.totalLabel = Object.keys(g.totals)
          .map(code => this.formatAmount(g.totals[code], { code }))
          .join(' · ')
      })
      return arr
    },
    sortedGroups () {
      return sortItems(this.groups, this.sortOptions.find(o => o.value === this.sortBy))
    },
    totalPages () { return Math.ceil(this.sortedGroups.length / this.pageSize) },
    pageFrom () { return this.sortedGroups.length ? (this.page - 1) * this.pageSize + 1 : 0 },
    pageTo () { return Math.min(this.page * this.pageSize, this.sortedGroups.length) },
    pagedGroups () { return this.sortedGroups.slice((this.page - 1) * this.pageSize, this.page * this.pageSize) },
    // Totales globales del scope (periodo + local), por moneda — PEN y USD nunca se mezclan.
    periodTotals () {
      const totals = {}
      for (const s of this.scopedList) {
        const code = s.currency && s.currency.code ? s.currency.code : 'PEN'
        totals[code] = (totals[code] || 0) + (s.total || 0)
      }
      const codes = Object.keys(totals)
      if (!codes.length) return [{ code: 'PEN', label: this.formatAmount(0, { code: 'PEN' }) }]
      return codes.map(code => ({ code, label: this.formatAmount(totals[code], { code }) }))
    },
    tenantsCount () {
      const set = new Set()
      for (const s of this.scopedList) {
        const c = s.client || {}
        set.add(c.id || c.document || c.alias || c.name || 'sin')
      }
      return set.size
    },
    rangeLabel () {
      return (this.dateFrom && this.dateTo) ? this.dateFrom + ' a ' + this.dateTo : 'Todo'
    }
  },
  watch: {
    search () { this.page = 1 },
    sortBy () { this.page = 1 },
    filterLocal () { this.page = 1 }
  },
  async created () { await this.getEstates() },
  methods: {
    formatDate (d) { return moment(d).locale('es').format('DD/MMM/YY') },
    // Mismo formato que el monto de recaudación (símbolo + miles + 2 decimales),
    // pero en la moneda nativa: S/ para soles, $ para dólares (nunca se convierte).
    formatAmount (total, currency) {
      const code = currency && currency.code ? currency.code : 'PEN'
      const locale = code === 'PEN' ? 'es-PE' : 'en-US'
      return Intl.NumberFormat(locale, { style: 'currency', currency: code }).format(total || 0)
    },
    productCount (s) { return Array.isArray(s.products) ? s.products.length : 0 },
    isExpanded (id) { return this.expandedIds.includes(id) },
    toggleGroup (id) {
      const i = this.expandedIds.indexOf(id)
      if (i === -1) this.expandedIds.push(id)
      else this.expandedIds.splice(i, 1)
    },
    openUpdate (sale) {
      this.sidebarData = sale
      this.showUpdateSidebar = true
    },
    addSidebar (obj) { this.list.unshift(obj); this.page = 1 },
    updateSidebar (o) {
      const idx = this.list.findIndex(d => d.id === o.id)
      if (idx !== -1) Object.assign(this.list[idx], o)
    },
    // Carga clientes solo la primera vez que se abre el sidebar de nueva venta
    async openNewSale () {
      if (!this.clientsLoaded) {
        try {
          this.$store.dispatch('showProgress')
          const snap = await db.collection('clients').orderBy('alias').get()
          snap.forEach(doc => this.clients.push({ id: doc.id, ...doc.data() }))
          this.clientsLoaded = true
        } catch (e) {
          this.$toast.error('Error al cargar expositores')
          return
        } finally {
          this.$store.dispatch('hideProgress')
        }
      }
      this.showNewSidebar = true
    },
    // Recibe { preset, from, to } del <DateFilter> y corre la consulta:
    // 'Todo' (o sin rango) → todas las ventas; cualquier otro → rango por fecha.
    onDateFilterChange ({ preset, from, to }) {
      this.dateFrom = from
      this.dateTo = to
      if (preset === 'all' || (!from && !to)) {
        this.loadAll()
      } else {
        this.applyFilter()
      }
    },
    async applyFilter () {
      try {
        this.$store.dispatch('showProgress')
        this.list = []
        this.page = 1
        this.expandedIds = []
        const start = new Date(this.dateFrom + 'T00:00:00')
        const end = new Date(this.dateTo + 'T23:59:59')
        const snap = await db.collection('sales')
          .where('dateOfIssue', '>=', Timestamp.fromDate(start))
          .where('dateOfIssue', '<=', Timestamp.fromDate(end))
          .orderBy('dateOfIssue', 'desc').get()
        snap.forEach(doc => {
          const obj = { id: doc.id, ...doc.data() }
          obj.dateOfIssue = doc.data().dateOfIssue ? doc.data().dateOfIssue.toDate() : null
          this.list.push(obj)
        })
      } catch (e) {
        this.$toast.error('Error al filtrar')
      } finally {
        this.$store.dispatch('hideProgress')
      }
    },
    async loadAll () {
      try {
        this.$store.dispatch('showProgress')
        this.list = []
        this.page = 1
        this.expandedIds = []
        const snap = await db.collection('sales').orderBy('dateOfIssue', 'desc').get()
        snap.forEach(doc => {
          const obj = { id: doc.id, ...doc.data() }
          obj.dateOfIssue = doc.data().dateOfIssue ? doc.data().dateOfIssue.toDate() : null
          this.list.push(obj)
        })
      } catch (e) {
        this.$toast.error('Error al cargar ventas')
      } finally {
        this.$store.dispatch('hideProgress')
      }
    },
    async getEstates () {
      try {
        const snap = await db.collection('estates').orderBy('createdAt').get()
        this.estates = []
        snap.forEach(doc => this.estates.push({ id: doc.id, ...doc.data() }))
      } catch (e) {
        // El dropdown de local es secundario; no bloquea la vista.
      }
    },
    exportToExcel () {
      if (!this.groups.length) { this.$toast.error('No hay ventas para exportar'); return }
      import('@/vendor/Export2Excel').then(excel => {
        const data = this.groups.map(g => [
          g.name,
          g.local,
          g.count,
          g.products,
          g.totals.PEN || 0,
          g.totals.USD || 0
        ])
        excel.export_json_to_excel({
          header: ['Expositor', 'Local', 'N° comprobantes', 'N° productos', 'Total S/', 'Total $'],
          data,
          filename: 'Ventas por expositor ' + this.rangeLabel,
          autoWidth: true,
          bookType: 'xlsx'
        })
      }).catch(() => this.$toast.error('Error al exportar'))
    },
    printReport (g) {
      const sales = g.sales.slice().sort((a, b) => (a.dateOfIssue ? a.dateOfIssue.getTime() : 0) - (b.dateOfIssue ? b.dateOfIssue.getTime() : 0))
      if (!sales.length) { this.$toast.error('No hay ventas en el periodo'); return }
      const fmt = d => {
        const x = d instanceof Date ? d : new Date(d)
        return String(x.getDate()).padStart(2, '0') + '/' + String(x.getMonth() + 1).padStart(2, '0') + '/' + x.getFullYear()
      }
      const totalsByCode = {}
      const rows = sales.map(s => {
        const code = s.currency && s.currency.code ? s.currency.code : 'PEN'
        totalsByCode[code] = (totalsByCode[code] || 0) + (s.total || 0)
        return `<tr><td>${fmt(s.dateOfIssue)}</td><td class="center">${this.productCount(s)}</td><td class="right">${this.formatAmount(s.total, s.currency)}</td></tr>`
      }).join('')
      const grandTotal = Object.keys(totalsByCode).map(code => this.formatAmount(totalsByCode[code], { code })).join(' · ')
      const alias = g.name
      const local = g.local && g.local !== '—' ? g.local : ''
      const period = this.rangeLabel
      const printTrigger = '<script>window.onload=function(){window.focus();window.print();}<' + '/script>'
      const html = `<!DOCTYPE html><html lang="es"><head><meta charset="utf-8"><title>Reporte — ${alias}</title>
<style>*{box-sizing:border-box}body{font-family:-apple-system,sans-serif;color:#18181B;margin:0;padding:40px}
.header{border-bottom:3px solid #B51A3E;padding-bottom:16px;margin-bottom:24px}
.brand{color:#B51A3E;font-size:12px;font-weight:700;letter-spacing:.08em;text-transform:uppercase}
h1{font-size:22px;margin:6px 0 12px;font-weight:700}.meta{font-size:12px;color:#71717A;line-height:1.7}
.meta strong{color:#18181B}table{width:100%;border-collapse:collapse;margin-top:20px;font-size:13px}
thead th{text-align:left;text-transform:uppercase;font-size:10px;letter-spacing:.06em;color:#A1A1AA;border-bottom:1px solid #E4E4E7;padding:10px}
tbody td{padding:10px;border-bottom:.5px solid #E4E4E7}.center{text-align:center}.right{text-align:right}
tfoot td{padding:14px 10px;font-weight:700;border-top:2px solid #18181B}
.footer{margin-top:40px;font-size:10px;color:#A1A1AA;text-align:center}
@media print{body{padding:24px}}</style></head><body>
<div class="header"><div class="brand">Inqi · Reporte de ventas</div><h1>${alias}</h1>
<div class="meta">${g.document ? `<div><strong>Documento:</strong> ${g.document}</div>` : ''}
${local ? `<div><strong>Local:</strong> ${local}</div>` : ''}
<div><strong>Periodo:</strong> ${period}</div><div><strong>Generado:</strong> ${fmt(new Date())}</div></div></div>
<table><thead><tr><th>Fecha de emisión</th><th class="center">N° productos</th><th class="right">Total</th></tr></thead>
<tbody>${rows}</tbody>
<tfoot><tr><td>Total general (${sales.length} ventas)</td><td></td><td class="right">${grandTotal}</td></tr></tfoot></table>
<div class="footer">Reporte generado desde Inqi</div>${printTrigger}</body></html>`

      const win = window.open('', '_blank')
      if (!win) { this.$toast.error('Habilita las ventanas emergentes para ver el reporte'); return }
      win.document.open()
      win.document.write(html)
      win.document.close()
    }
  }
}
</script>

<style scoped>
/* La cabecera de ventas es una sola fila (sin barra ni pie de progreso, a diferencia
   de recaudación): monto + chevron en horizontal y centrados verticalmente contra el
   nombre/local, en vez del apilado en columna que hereda .prop-row__right. */
.prop-row__head {
  align-items: center;
  margin-bottom: 0;
}
.prop-row__right {
  flex-direction: row;
  align-items: center;
  gap: 12px;
}

/* Pie de la sub-lista expandida — acción de reporte por expositor */
.collection-sub__foot {
  display: flex;
  justify-content: flex-end;
  padding: 10px 24px 14px;
}
</style>
